import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { User } from "@/hooks/use-users";
import { useChangeRole } from "@/hooks/use-users";
import { APP_ROLE_DESCRIPTIONS, APP_ROLES, type AppRole, isAppRole } from "@/schemas/users";

interface ChangeRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User;
}

export function ChangeRoleDialog({ open, onOpenChange, user }: ChangeRoleDialogProps) {
  // `user.role` is a plain DB string; narrow it rather than casting. An
  // unrecognised legacy value falls back to the least-privileged tier so the
  // picker never submits something the API would reject.
  const [role, setRole] = useState<AppRole>(isAppRole(user.role) ? user.role : "user");
  const changeRole = useChangeRole();

  function handleSubmit() {
    if (role === user.role) {
      onOpenChange(false);
      return;
    }

    changeRole.mutate({ userId: user.id, role }, { onSuccess: () => onOpenChange(false) });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change Role</DialogTitle>
          <DialogDescription>Update the role for {user.name}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Role</Label>
          <Select
            value={role}
            onValueChange={(next) => {
              if (isAppRole(next)) setRole(next);
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {APP_ROLES.map((appRole) => (
                <SelectItem key={appRole.value} value={appRole.value}>
                  {appRole.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-muted-foreground text-xs">{APP_ROLE_DESCRIPTIONS[role]}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={changeRole.isPending}>
            {changeRole.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
